import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../../../../ui/components/Button";
import { useDocumentChangeReports } from "../hooks/useDocumentChangeReports";
import {
  DocumentChangeAuditTable,
  DocumentChangeFilters,
  DocumentChangeSummaryCards,
  MostEditedFieldsPanel,
  type DocumentChangeFiltersState,
} from "../components/document-change-reports";
import type { DocumentChangeAuditQueryParams } from "../../domain/entities/DocumentChangeAuditEntity";

const DEFAULT_FILTERS: DocumentChangeFiltersState = {
  entityType: "",
};

const toQueryParams = (
  filters: DocumentChangeFiltersState,
  offset = 0,
): DocumentChangeAuditQueryParams => ({
  entityType: filters.entityType,
  limit: 25,
  offset,
});

export const DocumentChangeReportsPage: React.FC = () => {
  const { audits, summary, loading, error, pagination, fetchReports } =
    useDocumentChangeReports();
  const [filters, setFilters] =
    useState<DocumentChangeFiltersState>(DEFAULT_FILTERS);

  const loadReports = useCallback(
    async (nextFilters: DocumentChangeFiltersState, offset = 0) => {
      await fetchReports(toQueryParams(nextFilters, offset));
    },
    [fetchReports],
  );

  useEffect(() => {
    void loadReports(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, loadReports]);

  const handleFiltersChange = (next: DocumentChangeFiltersState) => {
    setFilters(next);
  };

  const handleLoadMore = async () => {
    if (!pagination.hasMore || loading) return;
    await loadReports(filters, pagination.offset + pagination.limit);
  };

  return (
    <div className="flex w-full flex-col items-start gap-6 p-8">
      <div className="flex w-full flex-col items-start gap-1">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Document Change Reports
        </span>
        <span className="text-body font-body text-subtext-color">
          Track OCR extraction accuracy and manual corrections on posted bills
          and invoices
        </span>
      </div>

      <DocumentChangeSummaryCards
        summary={summary}
        entityTypeFilter={filters.entityType}
      />

      <MostEditedFieldsPanel
        summary={summary}
        entityTypeFilter={filters.entityType}
      />

      <DocumentChangeFilters
        filters={filters}
        onChange={handleFiltersChange}
        loading={loading}
      />

      {error ? (
        <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-solid border-red-200 bg-red-50 p-4">
          <span className="text-body font-body text-red-600">
            {error.message}
          </span>
        </div>
      ) : null}

      <div className="flex w-full flex-col items-start gap-3">
        <div className="flex w-full items-center justify-between">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Audit log
          </span>
          <span className="text-caption font-caption text-subtext-color">
            Showing {audits.length} of {pagination.total} records
          </span>
        </div>

        <DocumentChangeAuditTable audits={audits} loading={loading} />

        {pagination.hasMore ? (
          <Button
            variant="neutral-secondary"
            onClick={() => {
              void handleLoadMore();
            }}
            disabled={loading}
          >
            {loading ? "Loading..." : "Load more"}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
